import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { StyledLink } from "../../components/CustomMUI";
import { Connector, DOT, LevelDot, LINE, START, STEP } from "./BadgeLevels";

// Dealt-with steps (done or skipped) first, in their own order — so a skipped
// step keeps its number — then the next step, then the rest at the bottom.
// Array.sort is stable, so equal ranks keep the backend's step order.
const STATE_ORDER = { done: 0, skipped: 0, current: 1, todo: 2 };

// Step label | circle | step name. Same circles and lines as the badge
// ladder, but offset left (a narrow label column) to give the names room.
// Rows align to their first line, since the next step's name has Skip beneath.
const ROW_SX = {
  display: "grid",
  gridTemplateColumns: `52px ${DOT}px 1fr`,
  columnGap: 1.5,
  alignItems: "start",
};
const LINE_HEIGHT = 20; // body2
const DOT_OFFSET = `${(LINE_HEIGHT - DOT) / 2}px`; // centres the circle on line one

const TEXT_COLOURS = {
  done: "text.primary",
  skipped: "text.secondary",
  todo: "text.secondary",
};

/**
 * Getting started's steps in its details popover, as a ladder like
 * BadgeLevels: "Step 1", 2…, "Step N" on the left, the step on the right.
 * Done steps tick in one by one on open, then the next step's ring lights up
 * and ripples; its name links to the step, with the only Skip beneath.
 * Instant with reduced motion.
 */
export default function ChecklistSteps({ row, onAction, onSkip }) {
  const reduceMotion = useReducedMotion();
  const pace = reduceMotion ? 0 : 1;
  const items = [...row.details.items].sort(
    (a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state],
  );
  // Skipped steps fill like done ones (green, no tick), counting towards the
  // ladder as they do the dashboard bar
  const isDealt = (item) => item?.state === "done" || item?.state === "skipped";
  const dealtCount = items.filter(isDealt).length;

  // When each done step ticks; the next step lights up after the last
  const dotDelay = (n) => (START + (n - 1) * STEP) * pace;
  const currentDelay = dotDelay(dealtCount + 1);

  return (
    <Box sx={{ py: 0.5 }}>
      {items.map((item, index) => {
        const n = index + 1;
        const isFilled = isDealt(item);
        const isCurrent = item.state === "current";
        const next = items[index + 1];
        // The line out of this step fills if the next one is done, just
        // before the connector below it (see Connector's delay there)
        const isNextDone = isDealt(next);
        const nextLineDelay = dotDelay(n + 1) - 0.12 * pace;
        const label = n === 1 || n === items.length ? `Step ${n}` : n;

        return (
          <Box key={item.key}>
            {index > 0 && (
              <Connector
                rowSx={ROW_SX}
                height={LINE}
                fill={isFilled ? 1 : 0}
                delay={dotDelay(n) - 0.08 * pace}
                duration={0.08 * pace}
              />
            )}

            <Box sx={ROW_SX}>
              <Typography
                variant="body2"
                textAlign="right"
                color={isFilled || isCurrent ? "text.primary" : "text.disabled"}
                fontWeight={isCurrent ? "bold" : undefined}
              >
                {label}
              </Typography>
              {/* Stretches the full row: a row can run to two lines (the next
                  step has Skip beneath), so the line continues from the
                  circle down to the connector below rather than leaving a gap */}
              <Box
                sx={{
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: DOT_OFFSET,
                }}
              >
                <LevelDot
                  reached={isFilled}
                  highlighted={isCurrent}
                  hideTick={item.state === "skipped"}
                  ripple={isCurrent}
                  delay={isFilled ? dotDelay(n) : currentDelay}
                  reduceMotion={reduceMotion}
                />
                {next && (
                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      width: 2,
                      bgcolor: "secondary.main",
                      overflow: "hidden",
                    }}
                  >
                    {isNextDone && (
                      <Box
                        component={motion.div}
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        transition={{
                          delay: Math.max(0, nextLineDelay),
                          duration: 0.04 * pace,
                        }}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "primary.main",
                          transformOrigin: "top",
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              {isCurrent ? (
                <Box>
                  <StyledLink
                    component="button"
                    variant="body2"
                    fontWeight="bold"
                    onClick={onAction}
                    sx={{ textAlign: "left" }}
                  >
                    {item.label}
                  </StyledLink>
                  {row.canSkip && (
                    <Box>
                      <StyledLink
                        component="button"
                        variant="caption"
                        onClick={onSkip}
                        sx={{ color: "text.disabled" }}
                      >
                        Skip
                      </StyledLink>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color={TEXT_COLOURS[item.state]}>
                  {item.label}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
